import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    IconButton,
    Box,
} from '@mui/material';
import { Close as CloseIcon, Room as RoomIcon } from '@mui/icons-material';

const ROILabelingModal = ({
    open,
    onClose,
    onSave,
    polygon = [],
    existingNames = [],
    loading = false,
}) => {
    const [roomName, setRoomName] = useState('');
    const [error, setError] = useState('');

    // Reset state when modal opens/closes
    useEffect(() => {
        if (open) {
            setRoomName('');
            setError('');
        }
    }, [open]);

    const validateRoomName = (name) => {
        const trimmedName = name.trim();

        if (!trimmedName) {
            return 'Room name is required';
        }

        if (trimmedName.length < 2) {
            return 'Room name must be at least 2 characters long';
        }

        if (trimmedName.length > 100) {
            return 'Room name must be less than 100 characters';
        }

        if (existingNames.includes(trimmedName)) {
            return 'A room with this name already exists';
        }

        return '';
    };

    const handleRoomNameChange = (event) => {
        const value = event.target.value;
        setRoomName(value);
        setError(validateRoomName(value));
    };

    const handleSave = () => {
        const validationError = validateRoomName(roomName);
        if (validationError) {
            setError(validationError);
            return;
        }

        onSave({
            name: roomName.trim(),
            polygon: polygon,
        });
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && !error && roomName.trim()) {
            handleSave();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={loading}
            PaperProps={{
                sx: {
                    borderRadius: { xs: 0, sm: 2 },
                    maxWidth: { xs: '100vw', sm: 450 },
                    maxHeight: { xs: '100vh', sm: 'none' },
                    margin: { xs: 0, sm: 2 },
                    width: { xs: '100%', sm: 'auto' },
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: { xs: 2, sm: 3 },
                    pb: { xs: 1.5, sm: 2 },
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                    <RoomIcon color="primary" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                    <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                    >
                        Name Room
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    disabled={loading}
                    size="small"
                    sx={{
                        '&:hover': {
                            backgroundColor: 'action.hover',
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 3, sm: 5 }, pb: { xs: 2, sm: 4 } }}>
                <TextField
                    fullWidth
                    label="Room Name"
                    variant="outlined"
                    value={roomName}
                    onChange={handleRoomNameChange}
                    onKeyPress={handleKeyPress}
                    error={!!error}
                    helperText={error || 'Enter a name for this room'}
                    disabled={loading}
                    autoFocus
                    placeholder="e.g., Office 101, Conference Room A"
                    sx={{
                        mt: { xs: 2, sm: 3 },
                        '& .MuiOutlinedInput-root': {
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            '&:hover fieldset': {
                                borderColor: 'primary.main',
                            },
                        },
                        '& .MuiInputLabel-root': {
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                        },
                        '& .MuiFormHelperText-root': {
                            fontSize: { xs: '0.75rem', sm: '0.8rem' },
                        },
                    }}
                />
            </DialogContent>

            <DialogActions
                sx={{
                    p: { xs: 2, sm: 3 },
                    pt: { xs: 1.5, sm: 2 },
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'center',
                    gap: { xs: 1, sm: 1.5 },
                    alignItems: 'stretch',
                    '& .MuiButton-root': {
                        mx: 0, // Remove any default margins
                    },
                }}
            >
                <Button
                    onClick={handleSave}
                    disabled={loading || !!error || !roomName.trim()}
                    variant="contained"
                    color="primary"
                    sx={{
                        minWidth: { xs: 'auto', sm: 120 },
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: { xs: '0.9rem', sm: '0.9rem' },
                        py: { xs: 1.5, sm: 1.25 },
                        px: { xs: 3, sm: 3 },
                        mx: 0,
                        order: { xs: 1, sm: 1 },
                    }}
                >
                    {loading ? 'Saving...' : 'Save'}
                </Button>
                <Button
                    onClick={onClose}
                    disabled={loading}
                    variant="outlined"
                    sx={{
                        minWidth: { xs: 'auto', sm: 100 },
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: { xs: '0.85rem', sm: '0.875rem' },
                        py: { xs: 1.5, sm: 1 },
                        px: { xs: 2, sm: 2 },
                        mx: 0,
                        order: { xs: 2, sm: 2 },
                    }}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ROILabelingModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    polygon: PropTypes.array,
    existingNames: PropTypes.array,
    loading: PropTypes.bool,
};

export default ROILabelingModal;
